import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { CompactPromptBox } from '../components/CompactPromptBox';
import { AskAnchorButton } from '../components/AskAnchorButton';
import { FeedCardSkeleton } from '../components/Skeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { getToolIcon } from '../lib/tool-icons';
import { useChat } from '../context/ChatContext';
import api from '../lib/api';
import { cn, formatDate, getInitials } from '../lib/utils';
import { toast } from 'sonner';
import {
  Folder,
  ChevronDown,
  ChevronRight,
  Search,
  X,
  Check,
  Filter,
  Inbox,
  Star,
  Layers,
  Clock,
  ExternalLink,
  Archive,
  MessageSquare,
} from 'lucide-react';

const FILTER_TABS = [
  { id: 'all', label: 'All', icon: Inbox },
  { id: 'unread', label: 'Unread', icon: Layers },
  { id: 'saved', label: 'Saved', icon: Star },
];

export default function FeedPage() {
  const chat = useChat();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProjectFilters, setSelectedProjectFilters] = useState([]);
  const [expandedTopics, setExpandedTopics] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  // Separate from isScrolled: the header must stop being see-through as
  // soon as content slides under it, which happens long before it collapses.
  const [hasContentBehind, setHasContentBehind] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isHoveringChips, setIsHoveringChips] = useState(false);
  const scrollContainerRef = useRef(null);
  const headerRef = useRef(null);
  const prevHeaderHeightRef = useRef(null);
  const expandedHeaderHeightRef = useRef(240);
  const suppressScrollRef = useRef(false);
  const dropdownRef = useRef(null);

  // --- Data fetching ---
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [eventsRes, projectsRes] = await Promise.all([
        api.get('/events'),
        api.get('/projects'),
      ]);
      setEvents(eventsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load feed:', error);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Action handlers ---
  const handleStarToggle = async (eventId, currentStarred) => {
    try {
      await api.put(`/events/${eventId}/status`, { starred: !currentStarred });
      setEvents(prev =>
        prev.map(e => (e.id === eventId ? { ...e, starred: !currentStarred } : e))
      );
      toast.success(currentStarred ? 'Removed from starred' : 'Starred');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleMarkRead = async (eventId) => {
    try {
      await api.put(`/events/${eventId}/status`, { status: 'read' });
      setEvents(prev =>
        prev.map(e => (e.id === eventId ? { ...e, status: 'read' } : e))
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleArchive = async (eventId) => {
    try {
      await api.put(`/events/${eventId}/status`, { status: 'archived' });
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Archived');
    } catch (error) {
      toast.error('Failed to archive');
    }
  };

  const handleAISearch = (query) => {
    if (!query.trim()) return;
    const projectId =
      selectedProjectFilters.length === 1 ? selectedProjectFilters[0] : null;
    chat.startNewChat(query, projectId);
  };

  const toggleProjectFilter = (id) => {
    setSelectedProjectFilters(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const removeProjectFilter = (id, e) => {
    e.stopPropagation();
    setSelectedProjectFilters(prev => prev.filter(f => f !== id));
  };

  const toggleTopicExpand = (id) => {
    setExpandedTopics(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const openAskAnchor = (eventId, e) => {
    e.stopPropagation();
    if (chat.panelOpen) {
      chat.closePanel();
    } else {
      // Open an empty chat panel (optionally with project context)
      const projectId =
        selectedProjectFilters.length === 1 ? selectedProjectFilters[0] : null;
      chat.startNewChat('', projectId);
    }
  };

  // --- Helper: map event_type to tool-specific icon ---
  const getEventIcon = (eventType) => {
    const toolMapping = {
      message: 'slack',
      issue_update: 'jira',
      pr_update: 'github',
      pr_merged: 'github',
    };
    const toolId = toolMapping[eventType];
    const IconComponent = getToolIcon(toolId || '');
    return <IconComponent className="h-4 w-4" />;
  };

  // --- Memoized derived data ---
  const groupedEvents = useMemo(() => {
    let filtered = events;
    if (activeFilter === 'unread')
      filtered = filtered.filter(e => e.status === 'unread');
    if (activeFilter === 'saved') filtered = filtered.filter(e => e.starred);

    const groups = {};
    filtered.forEach(event => {
      const pid = event.project_id;
      if (!groups[pid]) groups[pid] = [];
      groups[pid].push(event);
    });

    return Object.entries(groups)
      .map(([projectId, evts]) => {
        const project = projects.find(p => p.id === projectId);
        return {
          projectId,
          projectName: project?.name || 'Unknown',
          projectColor: project?.color || '#6366f1',
          events: evts,
        };
      })
      .filter(
        g =>
          selectedProjectFilters.length === 0 ||
          selectedProjectFilters.includes(g.projectId)
      )
      .sort((a, b) => {
        const latestA = Math.max(...a.events.map(e => new Date(e.timestamp)));
        const latestB = Math.max(...b.events.map(e => new Date(e.timestamp)));
        return latestB - latestA;
      });
  }, [events, projects, activeFilter, selectedProjectFilters]);


  // --- Scroll detection ---
  // Two thresholds, not one: collapsing the header removes ~167px from the
  // flow, which can push scrollTop back under a single threshold and flip the
  // state straight back -- that feedback loop is what made the header jitter.
  // The dead zone between COLLAPSE_AT and EXPAND_AT makes a flip stable.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const EXPAND_AT = 12;
    let frame = null;

    const handleScroll = () => {
      if (frame !== null) return; // coalesce bursts into one update per frame
      frame = requestAnimationFrame(() => {
        frame = null;
        // Ignore the scroll event caused by our own compensation below.
        if (suppressScrollRef.current) return;
        const y = container.scrollTop;
        setHasContentBehind(y > 2);
        // Collapse only once the expanded header has been scrolled through, so
        // the compensation has room to run without clamping at the top.
        const collapseAt = Math.max(160, expandedHeaderHeightRef.current);
        setIsScrolled((prev) => {
          if (!prev && y > collapseAt) return true;
          if (prev && y < EXPAND_AT) return false;
          return prev;
        });
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // --- Click outside closes dropdown ---
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // --- Filtered project options for dropdown ---
  const filteredProjectOptions = useMemo(() => {
    if (!projectSearchQuery.trim()) return projects;
    const q = projectSearchQuery.toLowerCase();
    return projects.filter(p => p.name.toLowerCase().includes(q));
  }, [projects, projectSearchQuery]);

  // --- Header display logic ---
  const showCompactHeader = isScrolled && !isHeaderHovered;

  // Keep the reading position anchored across a header swap. The two header
  // variants differ by ~167px (240 -> 73), and that height leaves/enters the flow
  // instantly -- without this the feed content visibly lurches on every toggle.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const header = headerRef.current;
    if (!container || !header) return;
    const prev = prevHeaderHeightRef.current;
    const next = header.offsetHeight;
    prevHeaderHeightRef.current = next;
    if (!showCompactHeader) expandedHeaderHeightRef.current = next;
    if (prev === null || prev === next) return;

    const target = container.scrollTop + (next - prev);
    // If there is not enough room above to absorb the delta, leave the scroll
    // position alone -- clamping to 0 would yank the reader to the top, which
    // is worse than the shift it is meant to hide.
    if (target < 0) return;
    suppressScrollRef.current = true;
    container.scrollTop = target;
    requestAnimationFrame(() => {
      suppressScrollRef.current = false;
    });
  }, [showCompactHeader]);

  const selectedProjectsList = selectedProjectFilters
    .map(id => projects.find(p => p.id === id))
    .filter(Boolean);

  const showCollapsed = selectedProjectsList.length > 3 && !isHoveringChips;
  const visibleChips = showCollapsed
    ? selectedProjectsList.slice(0, 2)
    : selectedProjectsList;

  // --- Project dropdown renderer (shared between compact and expanded headers) ---
  const renderProjectDropdown = (compact = false) => (
    <div className="relative" ref={dropdownRef}>
      {selectedProjectFilters.length === 0 ? (
        <button
          onClick={() => setIsProjectDropdownOpen(prev => !prev)}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl border border-black/[0.06] dark:border-white/[0.08]',
            'bg-white/60 dark:bg-white/[0.06] hover:bg-white/80 dark:hover:bg-white/[0.1]',
            'transition-all duration-200',
            compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
          )}
        >
          <Folder className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">All Projects</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      ) : (
        <div
          className="inline-flex items-center gap-1.5 flex-wrap"
          onMouseEnter={() => setIsHoveringChips(true)}
          onMouseLeave={() => setIsHoveringChips(false)}
        >
          {visibleChips.map(project => (
            <Badge
              key={project.id}
              variant="secondary"
              className="gap-1.5 pr-1 cursor-default"
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.color || '#6366f1' }}
              />
              <span className="truncate max-w-[100px]">{project.name}</span>
              <button
                onClick={(e) => removeProjectFilter(project.id, e)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {showCollapsed && (
            <Badge variant="outline" className="text-xs">
              +{selectedProjectsList.length - 2} more
            </Badge>
          )}
          <button
            onClick={() => setIsProjectDropdownOpen(prev => !prev)}
            className={cn(
              'inline-flex items-center justify-center rounded-lg',
              'border border-black/[0.06] dark:border-white/[0.08]',
              'bg-white/60 dark:bg-white/[0.06] hover:bg-white/80 dark:hover:bg-white/[0.1]',
              'transition-all duration-200',
              compact ? 'h-7 w-7' : 'h-8 w-8'
            )}
          >
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      )}

      {isProjectDropdownOpen && (
        <div className="absolute top-full right-0 mt-2 w-64 rounded-xl border border-black/[0.06] dark:border-white/[0.08] bg-white/90 dark:bg-neutral-900/90 backdrop-blur-xl shadow-lg z-20 py-2">
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearchQuery}
                onChange={(e) => setProjectSearchQuery(e.target.value)}
                className="w-full h-8 pl-8 pr-3 rounded-lg text-sm bg-black/[0.04] dark:bg-white/[0.06] border-none outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredProjectOptions.map(project => {
              const isSelected = selectedProjectFilters.includes(project.id);
              return (
                <button
                  key={project.id}
                  onClick={() => toggleProjectFilter(project.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors',
                    isSelected && 'bg-primary/5'
                  )}
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: project.color || '#6366f1' }}
                  />
                  <span className="flex-1 text-left truncate">{project.name}</span>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
            {filteredProjectOptions.length === 0 && (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                No projects found
              </p>
            )}
          </div>
          {selectedProjectFilters.length > 0 && (
            <div className="border-t border-black/[0.06] dark:border-white/[0.08] mt-1 pt-1 px-3">
              <button
                onClick={() => {
                  setSelectedProjectFilters([]);
                  setIsProjectDropdownOpen(false);
                }}
                className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <AppShell>
      <div className="flex flex-col h-full overflow-hidden" data-testid="feed-page">
        <div className="flex flex-1 min-h-0 overflow-hidden">
        {!(chat.panelOpen && chat.layout === 'fullscreen') && (
          <div className="flex flex-col min-w-0 h-full overflow-hidden flex-1">
            <main ref={scrollContainerRef} className="flex-1 overflow-y-auto">
              <div className="w-full px-6 md:px-10">
                {/* Sticky header */}
                <div
                  ref={headerRef}
                  className={cn(
                    'sticky top-0 z-10 -mx-6 md:-mx-10 px-6 md:px-10',
                    'transition-[background-color,border-color,backdrop-filter] duration-200',
                    hasContentBehind
                      ? 'backdrop-blur-xl bg-white/80 dark:bg-neutral-900/80 border-b border-black/[0.06] dark:border-white/[0.06]'
                      : ''
                  )}
                  onMouseEnter={() => setIsHeaderHovered(true)}
                  onMouseLeave={() => setIsHeaderHovered(false)}
                >
                  {/* Compact header */}
                  {showCompactHeader && (
                    <div className="flex items-center justify-between gap-4 py-3">
                      <h1 className="text-lg font-semibold text-foreground/90 flex-shrink-0">
                        Feed
                      </h1>
                      <SegmentedTabs
                        tabs={FILTER_TABS}
                        activeTab={activeFilter}
                        onTabChange={setActiveFilter}
                        className="flex-shrink-0"
                      />
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <AskAnchorButton
                          onClick={(e) => openAskAnchor(null, e)}
                          size="sm"
                          active={chat.panelOpen}
                        />
                        {renderProjectDropdown(true)}
                      </div>
                    </div>
                  )}

                  {/* Expanded header */}
                  {!showCompactHeader && (
                    <div className="pt-8 pb-4 space-y-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <h1 className="text-3xl font-bold text-foreground/90">
                            Feed
                          </h1>
                          <p className="text-muted-foreground mt-1">
                            All updates from your projects
                          </p>
                        </div>
                        {renderProjectDropdown(false)}
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <SegmentedTabs
                          tabs={FILTER_TABS}
                          activeTab={activeFilter}
                          onTabChange={setActiveFilter}
                        />
                        <AskAnchorButton
                          onClick={(e) => openAskAnchor(null, e)}
                          size="sm"
                          active={chat.panelOpen}
                        />
                      </div>

                      <CompactPromptBox
                        onSubmit={handleAISearch}
                        placeholder="Ask anything about your feed..."
                      />
                    </div>
                  )}
                </div>

                {/* Feed content */}
                <div className="space-y-6 py-6 pb-8">
                  {loading ? (
                    <>
                      <FeedCardSkeleton />
                      <FeedCardSkeleton />
                      <FeedCardSkeleton />
                    </>
                  ) : groupedEvents.length === 0 ? (
                    <Card className="animate-fade-in">
                      <CardContent className="p-8 text-center">
                        <p className="text-muted-foreground">No updates match your filters.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    groupedEvents.map((group, groupIndex) => (
                      <Card
                        key={group.projectId}
                        className="overflow-hidden border-none shadow-glass bg-white/40 dark:bg-white/5 animate-fade-up"
                        style={{ animationDelay: `${groupIndex * 80}ms` }}
                      >
                        {/* Group Header */}
                        <div className="px-6 py-4 border-b border-white/10 bg-white/20 dark:bg-white/5 flex items-center justify-between backdrop-blur-md">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-2 h-2 rounded-full shadow-sm"
                                style={{ backgroundColor: group.projectColor }}
                              />
                              <h2 className="font-semibold text-lg text-foreground/90">
                                {group.projectName}
                              </h2>
                            </div>
                            <span className="text-xs text-muted-foreground ml-5">
                              {group.events.length}{' '}
                              {group.events.length === 1 ? 'update' : 'updates'}
                            </span>
                          </div>
                        </div>

                        {/* Topics */}
                        <div className="py-2 space-y-1">
                          {group.events.map((event) => {
                            const isExpanded = expandedTopics.includes(event.id);
                            const isSaved = event.starred;

                            return (
                              <div key={event.id} className="group transition-colors">
                                <div
                                  className={cn(
                                    'rounded-xl mx-2 my-1 border transition-colors',
                                    'border-black/[0.08] dark:border-white/[0.06]',
                                    'bg-white/40 dark:bg-white/[0.02]',
                                    isExpanded && 'bg-white/50 dark:bg-white/[0.04]'
                                  )}
                                >
                                  {/* Clickable header */}
                                  <div
                                    className="p-4 cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.03] rounded-xl transition-colors"
                                    onClick={() => toggleTopicExpand(event.id)}
                                  >
                                    <div className="flex items-start gap-4">
                                      {/* Tool icon */}
                                      <div className="mt-1 h-8 w-8 rounded-xl bg-black/[0.04] dark:bg-white/[0.08] flex items-center justify-center text-muted-foreground shrink-0">
                                        {getEventIcon(event.event_type)}
                                      </div>

                                      <div className="flex-1 min-w-0">
                                        {/* Title row */}
                                        <div className="flex items-start justify-between mb-1">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <h3 className="font-medium text-sm text-foreground">
                                                {event.title}
                                              </h3>
                                              {event.status === 'unread' && (
                                                <span className="inline-flex items-center justify-center w-2 h-2 bg-primary rounded-full shadow-sm shadow-primary/50" />
                                              )}
                                            </div>
                                            <div className="flex items-center gap-2 text-muted-foreground mb-2">
                                              <span className="text-xs flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {formatDate(event.timestamp)}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Bookmark/Star button */}
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                              'h-8 w-8 -mr-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10',
                                              isSaved
                                                ? 'text-yellow-500'
                                                : 'text-muted-foreground opacity-0 group-hover:opacity-100'
                                            )}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleStarToggle(event.id, event.starred);
                                            }}
                                          >
                                            <Star
                                              className={cn(
                                                'h-4 w-4',
                                                isSaved && 'fill-current'
                                              )}
                                            />
                                          </Button>
                                        </div>

                                        {/* Summary */}
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                          {event.content}
                                        </p>

                                        {/* Author + expand button */}
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <Avatar className="h-6 w-6 border-2 border-white dark:border-neutral-900">
                                              <AvatarImage src={event.author_avatar} />
                                              <AvatarFallback>
                                                {getInitials(event.author_name || 'U')}
                                              </AvatarFallback>
                                            </Avatar>
                                            <span className="text-xs text-muted-foreground">
                                              {event.author_name}
                                            </span>
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs gap-1 text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10 rounded-lg"
                                          >
                                            {isExpanded ? 'Collapse' : 'View Details'}
                                            {isExpanded ? (
                                              <ChevronDown className="h-3 w-3" />
                                            ) : (
                                              <ChevronRight className="h-3 w-3" />
                                            )}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Expanded section */}
                                  {isExpanded && (
                                    <div className="px-4 pb-4 pt-0 pl-[4.5rem]">
                                      <div className="space-y-3 pt-3 border-t border-black/[0.05] dark:border-white/[0.06] pl-4">
                                        {/* Full content */}
                                        <p className="text-sm text-foreground/90 whitespace-pre-wrap">
                                          {event.content}
                                        </p>

                                        {/* External link if exists */}
                                        {event.external_url &&
                                          event.external_url !== '#' && (
                                            <div className="pt-3 border-t border-black/[0.05] dark:border-white/[0.06]">
                                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                                Connected Source
                                              </p>
                                              <a
                                                href={event.external_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-black/[0.08] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.04] hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors group/asset w-fit"
                                              >
                                                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-xs font-medium">
                                                  View Source
                                                </span>
                                              </a>
                                            </div>
                                          )}

                                        {/* Action buttons */}
                                        <div className="pt-3 flex items-center justify-end gap-2">
                                          {event.status === 'unread' && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 text-xs rounded-lg"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMarkRead(event.id);
                                              }}
                                            >
                                              Mark Read
                                            </Button>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 text-xs rounded-lg gap-1.5 text-muted-foreground"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleArchive(event.id);
                                            }}
                                          >
                                            <Archive className="h-3 w-3" />
                                            Archive
                                          </Button>
                                          {event.external_url &&
                                            event.external_url !== '#' && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 text-xs rounded-lg gap-1.5 text-muted-foreground"
                                                asChild
                                              >
                                                <a
                                                  href={event.external_url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <ExternalLink className="h-3 w-3" />
                                                  View in Source
                                                </a>
                                              </Button>
                                            )}
                                          <AskAnchorButton
                                            size="sm"
                                            active={chat.panelOpen}
                                            onClick={(e) => openAskAnchor(event.id, e)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </main>
          </div>
        )}

        </div>
      </div>
    </AppShell>
  );
}
